import { useQuery } from '@tanstack/react-query';

import {
  getRequest,
  paginationMapper,
  resolveQueryString,
} from 'src/utils/request';
import { API } from 'src/reactQuery/Api';
import { daysUntilToday, forceString } from 'src/utils/datetime';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';

import type { RequestDataArray } from 'src/types/requestData';

const mapUserAccounts = (
  userAccount: UCM.UserAccountRawType,
  userId: string,
): UCM.UserAccountType => {
  const prescreening = userAccount.prescreening ?? null;

  const prescreeningStatusMapper: Record<
    UCM.PrescreeningStatusRawType,
    UCM.PrescreeningStatusType
  > = {
    pending: 'Pending',
    approved: 'Approved',
    rejected_on_ratings: 'MICE rejected',
    rejected_on_technicalities: 'Language Rejected',
  };

  const accessStatusMapper = {
    queued: 'Queued',
    queued_rejected: 'Rejected',
  };

  return {
    id: userId,
    email: userAccount.email,
    userId: userAccount.user_id,
    firstName: userAccount.first_name,
    lastName: userAccount.last_name,
    userUuid: userAccount.user_uuid,
    createdAt: daysUntilToday(userAccount.created_at) ?? '',
    accessStatus:
      userAccount.access_status != null
        ? accessStatusMapper[userAccount.access_status]
        : null,
    phoneNumber:
      userAccount.phone.number === null &&
      userAccount.phone.calling_code === null
        ? null
        : `${userAccount.phone.calling_code ?? ''} ${userAccount.phone.number ?? ''}`.trim(),
    campaign: userAccount?.campaign ?? null,
    userPotential: userAccount?.user_potential ?? null,
    basicProfileCompletedAt: userAccount.basic_profile_completed_at
      ? daysUntilToday(userAccount.basic_profile_completed_at)
      : null,
    interviewCompletedAt: userAccount.interview_completed_at
      ? daysUntilToday(userAccount.interview_completed_at)
      : null,
    accessPausedAt: userAccount?.access_paused_at
      ? daysUntilToday(userAccount.access_paused_at)
      : null,
    accessPausedReasons: userAccount?.access_paused_reasons ?? null,
    stage: userAccount?.stage ?? null,
    stageTime:
      userAccount?.stage_time != null && userAccount?.stage_time != ''
        ? daysUntilToday(userAccount?.stage_time, true)
        : null,
    prescreening: prescreening
      ? {
          uuid: prescreening.uuid,
          showUrl: prescreening.show_url,
          status: prescreeningStatusMapper[prescreening.status],
          interviewManagerUrl: prescreening.interview_manager_url ?? null,
          verifiedBy: prescreening.verified_by ?? null,
          createdAt: prescreening.created_at
            ? daysUntilToday(prescreening.created_at)
            : null,
        }
      : null,
    birthDate: userAccount.birth_date
      ? forceString(userAccount.birth_date)
      : null,
    adultOn: userAccount.adult_on ? forceString(userAccount.adult_on) : null,
    workPermitted: userAccount.work_permitted ?? null,
    occupationCode: userAccount.occupation_code ?? null,
  };
};

const getUserAccounts = async <T extends UCM.CandidateStatusType>(
  candidateStatus: T,
  filters: Array<Array<{ value: string; name: string }>> = [],
  params: Record<string, string | number>,
) => {
  const userAcccountsEndpoint =
    candidateStatus === 'all'
      ? API.GET.getAllUserAccounts
      : API.GET.getUserAccounts(candidateStatus);

  const query = resolveQueryString(filters, params);

  const {
    data: { meta, data },
  } = await getRequest<
    RequestDataArray<UCM.UserAccountRawType, UCM.MetaRawType>
  >(`${userAcccountsEndpoint}${query}`);

  return {
    meta: paginationMapper(meta),
    data: data.map(({ attributes, id }) => mapUserAccounts(attributes, id)),
  };
};

export const useGetUserAccounts = (
  candidateStatus: UCM.CandidateStatusType,
  params: Record<string, string | number>,
  filters: Array<{ value: string; name: string }> = [],
  config: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_ACCOUNTS, candidateStatus, filters, params],
    queryFn: () => getUserAccounts(candidateStatus, [filters], params),
    staleTime: 1000 * 60 * 5, // Dashboards refreshed after every 5 minutes automatically
    ...config,
  });
};
